// src/auth.js

import { setInitData, getInitData } from "./services/commonHeaders";
import { API_URL } from './config/api';
import { perfDiag } from './utils/perfTracker';
import { feLog } from './utils/frontendLogger';

/**
 * Shared helper that calls the auth endpoint and returns parsed response.
 * @param {string} initData - Telegram initData string
 * @param {object} [opts] - Optional settings
 * @param {string} [opts.visitorId] - FingerprintJS visitor ID
 * @returns {{ token, locale, timezone, designVersion }}
 */
async function callAuthEndpoint(initData, opts = {}) {
    const userAgent = navigator.userAgent || '';
    const platform = window.Telegram?.WebApp?.platform || '';
    const version = window.Telegram?.WebApp?.version || '';

    const headers = {
        'Content-Type': 'application/json',
        'X-User-Agent': userAgent,
        'X-Telegram-Platform': platform,
        'X-Telegram-Version': version,
    };
    if (opts.visitorId) {
        headers['X-Visitor-Id'] = opts.visitorId;
    }

    const response = await fetch(API_URL + `v2/auth?initData=${encodeURIComponent(initData)}`, {
        method: 'GET',
        headers,
    });

    if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch (_) {
            errorData = { message: 'Unknown error occurred' };
        }

        const error = new Error('Authentication failed');
        error.response = {
            status: response.status,
            data: errorData
        };
        throw error;
    }

    const data = await response.json();
    return {
        token: data.token,
        locale: data.locale,
        timezone: data.timezone,
        designVersion: data.design_version || 2,
    };
}

export async function authenticate(initData) {

    const authStart = Date.now();

    // Try early auth promise started from index.html (overlaps with JS bundle download)
    if (window.__earlyAuthPromise) {
        try {
            const data = await window.__earlyAuthPromise;
            if (data && data.token) {
                setInitData(initData);
                perfDiag.authEarly = true;
                feLog('info', 'auth_ok', '', { elapsed_ms: String(Date.now() - authStart), early: 'true' });
                // Start FingerprintJS in background for future requests
                collectFingerprint();
                return {
                    token: data.token,
                    locale: data.locale,
                    timezone: data.timezone,
                    designVersion: data.design_version || 2
                };
            }
        } catch (e) {
            feLog('error', 'auth_early_failed', String(e));
        } finally {
            window.__earlyAuthPromise = null;
        }
    }

    // Start FingerprintJS in background — don't block auth (lazy-loaded)
    const fingerprintPromise = (async () => {
        try {
            const { default: FingerprintJS } = await import("@fingerprintjs/fingerprintjs");
            const fp = await FingerprintJS.load();
            const fpResult = await fp.get();
            return fpResult.visitorId;
        } catch (e) {
            console.warn('Fingerprint failed:', e);
            return '';
        }
    })();

    // Race fingerprint against 200ms timeout — proceed with auth either way
    const visitorId = await Promise.race([
        fingerprintPromise,
        new Promise(resolve => setTimeout(() => resolve(''), 200))
    ]);

    try {
        const result = await callAuthEndpoint(initData, { visitorId });
        setInitData(initData);
        feLog('info', 'auth_ok', '', { elapsed_ms: String(Date.now() - authStart) });
        return result;
    } catch (error) {
        feLog('error', 'auth_failed', String(error), { status: String(error.response?.status || '') });
        throw error;
    }
}

/**
 * Re-authenticate using stored initData to get a fresh JWT token.
 * Returns { token, locale, timezone, designVersion } on success, or null on failure.
 */
export async function refreshAuthToken() {
    const initData = getInitData();
    if (!initData) {
        feLog('warn', 'auth_refresh.no_initdata', '');
        return null;
    }

    try {
        const result = await callAuthEndpoint(initData);
        feLog('info', 'auth_refresh.ok', '');
        return result;
    } catch (error) {
        feLog('error', 'auth_refresh.error', String(error), { status: String(error.response?.status || '') });
        return null;
    }
}

// Fire-and-forget fingerprint collection for analytics (lazy-loaded)
function collectFingerprint() {
    import("@fingerprintjs/fingerprintjs")
        .then(({ default: FingerprintJS }) => FingerprintJS.load())
        .then(fp => fp.get())
        .catch(() => {});
}
